// src/App.js
import React, { useState, useEffect } from "react";
import { auth } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { db } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import MainChat from "./MainChat";

function App() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });

  // auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // load profile from firestore
        const docRef = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUser({ ...currentUser, ...docSnap.data() });
        } else {
          setUser(currentUser);
        }
      } else {
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  // ✅ Email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  // ✅ Password validation: min 6 chars, 1 number, 1 letter
  const validatePassword = (password) => {
    const re = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;
    return re.test(password);
  };

  // signup
  const handleSignup = async () => {
    let valid = true;
    let newErrors = { email: "", password: "" };

    if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address.";
      valid = false;
    }
    if (!validatePassword(password)) {
      newErrors.password =
        "Password must be at least 6 characters long and include at least 1 number.";
      valid = false;
    }

    setErrors(newErrors);
    if (!valid) return;

    try {
      const userCred = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const defaultPhoto = "https://i.pravatar.cc/150?u=" + email;
      await setDoc(doc(db, "users", userCred.user.uid), {
        name: email.split("@")[0],
        email,
        mobile: "",
        photoURL: defaultPhoto,
      });

      await updateProfile(userCred.user, {
        displayName: email.split("@")[0],
        photoURL: defaultPhoto,
      });

      alert("✅ User registered successfully!");
    } catch (err) {
      alert("⚠️ " + err.message);
    }
  };

  // login
  const handleLogin = async () => {
    let valid = true;
    let newErrors = { email: "", password: "" };

    if (!validateEmail(email)) {
      newErrors.email = "Invalid email format.";
      valid = false;
    }
    if (!password) {
      newErrors.password = "Password cannot be empty.";
      valid = false;
    }

    setErrors(newErrors);
    if (!valid) return;

    try {
      await signInWithEmailAndPassword(auth, email, password);
      alert("✅ Successfully logged in!");
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        alert("⚠️ Please sign up, you don’t have an account.");
      } else if (err.code === "auth/wrong-password") {
        alert("⚠️ Incorrect password. Try again.");
      } else {
        alert("⚠️ " + err.message);
      }
    }
  };

  if (!user) {
    return (
      <div className="login-bg">
        <div className="login-box">
          <h2>Chat App</h2>

          <input
            type="email"
            placeholder="Email"
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <p className="error-text">{errors.email}</p>}

          <input
            type="password"
            placeholder="Password"
            onChange={(e) => setPassword(e.target.value)}
          />
          {errors.password && <p className="error-text">{errors.password}</p>}

          <div className="btn-group">
            <button onClick={handleSignup}>Sign Up</button>
            <button onClick={handleLogin}>Login</button>
          </div>
        </div>

        {/* CSS styles */}
        <style>{`
          body, html, #root {
            height: 100%;
            margin: 0;
          }
          .login-bg {
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            background: url("https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1350&q=80")
              no-repeat center center/cover;
            position: relative;
          }
          .login-bg::after {
            content: "";
            position: absolute;
            top: 0; left: 0;
            width: 100%; height: 100%;
            background: rgba(0,0,0,0.6);
          }
          .login-box {
            position: relative;
            background: rgba(255, 255, 255, 0.10);
            padding: 30px;
            border-radius: 15px;
            box-shadow: 0 8px 25px rgba(0,0,0,0.3);
            z-index: 1;
            text-align: center;
            width: 320px;
            animation: dropDown 2s ease forwards;
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
          }
          .login-box h2 {
            margin-bottom: 20px;
            color: #fff;
          }
          .login-box input {
            width: 100%;
            padding: 10px;
            margin: 8px 0;
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 8px;
            outline: none;
            font-size: 14px;
            background: rgba(255, 255, 255, 0.3);
            color: #fff;
          }
          .login-box input::placeholder {
            color: #eee;
          }
          .error-text {
            color: #ff6b6b;
            font-size: 12px;
            margin: -5px 0 8px;
            text-align: left;
          }
          .btn-group {
            margin-top: 15px;
            display: flex;
            justify-content: space-between;
          }
          .btn-group button {
            flex: 1;
            margin: 0 5px;
            padding: 10px;
            border: none;
            border-radius: 8px;
            background: linear-gradient(45deg, #4caf50, #81c784);
            color: white;
            font-size: 14px;
            cursor: pointer;
            transition: transform 0.2s;
          }
          .btn-group button:hover {
            transform: scale(1.05);
          }
          @keyframes dropDown {
            0% { transform: translateY(-150%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return <MainChat currentUser={user} />;
}

export default App;
